import React, { useState } from "react";
import {
    X,
    FileCheck,
    User,
    Briefcase,
    Calendar,
    ChevronLeft,
    CheckCircle,
    XCircle,
    Clock,
} from "lucide-react";

const normalizeStatus = (value) =>
    String(value || "")
        .trim()
        .toUpperCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");

const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString("fr-FR");
};

const getCandidatName = (candidat, candidature) => {
    const fromCandidat = `${candidat?.nomCandidat || candidat?.nom || ""} ${candidat?.prenomCandidat || candidat?.prenom || ""}`.trim();
    const fromCandidature = `${candidature?.candidatNom || ""} ${candidature?.candidatPrenom || ""}`.trim();
    return fromCandidat || fromCandidature || "-";
};

const getPoste = (candidature) => {
    if (candidature?.autrePoste?.trim()) return candidature.autrePoste.trim();
    return candidature?.posteNom || candidature?.poste || "-";
};

const getOffreStatut = (candidature) =>
    candidature?.offreSalariale?.statutOffre || candidature?.statutOffre || "";

const getDecisionFinaleLabel = (decision) =>
    normalizeStatus(decision) === "A_REVOIR" ? "Refusé" : decision || "";

const firstNonBlank = (...values) =>
    values.find((value) => String(value || "").trim()) || "";

const getDemandeurName = (candidature) =>
    firstNonBlank(
        candidature?.demandeurNom,
        candidature?.demandeurName,
        candidature?.demandeRecrutement?.demandeurNom,
        candidature?.demandeRecrutement?.demandeur?.fullName,
        candidature?.demandeRecrutement?.demandeur?.firstName && candidature?.demandeRecrutement?.demandeur?.lastName
            ? `${candidature.demandeRecrutement.demandeur.firstName} ${candidature.demandeRecrutement.demandeur.lastName}`
            : ""
    ) || (candidature?.demandeurId ? `Utilisateur #${candidature.demandeurId}` : "—");

const getResponsableRhName = (candidature) => {
    const fromIntervenant = (candidature?.evaluationIntervenants || []).find(
        (item) => item?.designeParNom || item?.designePar
    );
    return firstNonBlank(
        candidature?.evaluationDesigneParNom,
        fromIntervenant?.designeParNom,
        candidature?.responsableRhNom,
        candidature?.rhResponsableNom
    ) || (candidature?.evaluationDesigneParId || fromIntervenant?.designePar
        ? `Utilisateur #${candidature?.evaluationDesigneParId || fromIntervenant?.designePar}`
        : "—");
};

const getIntervenantsLabel = (candidature) => {
    const names = (candidature?.evaluationIntervenants || [])
        .map((item) => item?.intervenantNom)
        .filter(Boolean);
    return names.length ? names.join(", ") : "—";
};

const buildOutcome = ({ state, positiveLabel, negativeLabel, pendingLabel, raw }) => {
    if (state === "positive") {
        return {
            state,
            label: positiveLabel,
            raw,
            bg: "#dcfce7",
            color: "#15803d",
            border: "#86efac",
            icon: CheckCircle,
        };
    }

    if (state === "negative") {
        return {
            state,
            label: negativeLabel,
            raw,
            bg: "#fee2e2",
            color: "#dc2626",
            border: "#fca5a5",
            icon: XCircle,
        };
    }

    return {
        state: "pending",
        label: pendingLabel,
        raw,
        bg: "#f9fefd",
        color: "#707ea7",
        border: "#a3b0d2",
        icon: Clock,
    };
};

const buildBlockedOutcome = (outcome) => ({
    ...outcome,
    state: "blocked",
    label: "Non applicable",
    raw: "",
    bg: "#f3f4f6",
    color: "#9ca3af",
    border: "#d1d5db",
    icon: Clock,
});

const getCvOutcome = (candidature) => {
    const statut = normalizeStatus(candidature?.statut || candidature?.statutDeCandidature);
    const statusValue = Number(candidature?.status);

    if (
        statusValue === 1 ||
        ["VALIDEE", "VALIDE", "APPROUVEE", "APPROUVE", "ACCEPTEE", "ACCEPTE"].includes(statut)
    ) {
        return buildOutcome({
            state: "positive",
            positiveLabel: "CV retenu",
            negativeLabel: "CV non retenu",
            pendingLabel: "CV en attente",
            raw: candidature?.statut || candidature?.statutDeCandidature || "",
        });
    }

    if (
        statusValue === 0 ||
        ["REFUSEE", "REFUSE", "REJETEE", "REJETE", "ANNULEE", "ANNULE"].includes(statut)
    ) {
        return buildOutcome({
            state: "negative",
            positiveLabel: "CV retenu",
            negativeLabel: "CV non retenu",
            pendingLabel: "CV en attente",
            raw: candidature?.statut || candidature?.statutDeCandidature || "",
        });
    }

    return buildOutcome({
        state: "pending",
        positiveLabel: "CV retenu",
        negativeLabel: "CV non retenu",
        pendingLabel: "CV en attente",
        raw: candidature?.statut || candidature?.statutDeCandidature || "",
    });
};

const getEntretienOutcome = (candidature) => {
    const decision = normalizeStatus(candidature?.decisionFinale);
    const decisionLabel = getDecisionFinaleLabel(candidature?.decisionFinale);

    if (decision === "VALIDE") {
        return buildOutcome({
            state: "positive",
            positiveLabel: "Entretien validé",
            negativeLabel: "Entretien non validé",
            pendingLabel: "Entretien en attente",
            raw: decisionLabel,
        });
    }

    if (["REFUSE", "REFUSEE", "A_REVOIR", "REJETE", "REJETEE", "NON_VALIDE", "NON_VALIDEE"].includes(decision)) {
        return buildOutcome({
            state: "negative",
            positiveLabel: "Entretien validé",
            negativeLabel: "Entretien non validé",
            pendingLabel: "Entretien en attente",
            raw: decisionLabel,
        });
    }

    return buildOutcome({
        state: "pending",
        positiveLabel: "Entretien validé",
        negativeLabel: "Entretien non validé",
        pendingLabel: "Entretien en attente",
        raw: decisionLabel,
    });
};

const getOffreOutcome = (candidature) => {
    const statut = normalizeStatus(getOffreStatut(candidature));

    if (["ACCEPTE", "ACCEPTEE"].includes(statut)) {
        return buildOutcome({
            state: "positive",
            positiveLabel: "Offre acceptée",
            negativeLabel: "Offre non acceptée",
            pendingLabel: "Offre en attente",
            raw: getOffreStatut(candidature),
        });
    }

    if (["REFUSE", "REFUSEE", "REJETE", "REJETEE"].includes(statut)) {
        return buildOutcome({
            state: "negative",
            positiveLabel: "Offre acceptée",
            negativeLabel: "Offre non acceptée",
            pendingLabel: "Offre en attente",
            raw: getOffreStatut(candidature),
        });
    }

    return buildOutcome({
        state: "pending",
        positiveLabel: "Offre acceptée",
        negativeLabel: "Offre non acceptée",
        pendingLabel: "Offre en attente",
        raw: getOffreStatut(candidature),
    });
};

const getRawOutcomes = (candidature) => [
    { key: "cv", title: "CV", ...getCvOutcome(candidature) },
    { key: "entretien", title: "Entretien", ...getEntretienOutcome(candidature) },
    { key: "offre", title: "Offre", ...getOffreOutcome(candidature) },
];

const getOutcomes = (candidature) => {
    let previousAllowsNext = true;

    return getRawOutcomes(candidature).map((outcome) => {
        if (!previousAllowsNext) {
            return buildBlockedOutcome(outcome);
        }

        previousAllowsNext = outcome.state === "positive";
        return outcome;
    });
};

const getRefusalDetails = (candidature) => {
    const refusedOutcome = getRawOutcomes(candidature).find((outcome) => outcome.state === "negative");
    if (!refusedOutcome) return null;

    const firstIntervenantComment = (candidature?.evaluationIntervenants || [])
        .map((item) => item?.commentaireGlobalIntervenant)
        .find((comment) => String(comment || "").trim());

    const motifByStep = {
        cv: firstNonBlank(candidature?.motifRejet, candidature?.motifAnnulation, candidature?.motif),
        entretien: firstNonBlank(
            candidature?.motifRefusEvaluation,
            candidature?.evaluationMotifRefus,
            candidature?.evaluationSyntheseGlobale,
            firstIntervenantComment
        ),
        offre: firstNonBlank(
            candidature?.offreSalariale?.commentaire,
            candidature?.commentaireOffre,
            candidature?.offreCommentaire
        ),
    };

    return {
        title: refusedOutcome.title,
        label: refusedOutcome.label,
        motif: motifByStep[refusedOutcome.key] || "",
    };
};

const OutcomeBadge = ({ outcome }) => {
    const Icon = outcome.icon;
    return (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "5px 10px",
                borderRadius: 20,
                fontSize: 11,
                fontWeight: 700,
                backgroundColor: outcome.bg,
                color: outcome.color,
                whiteSpace: "nowrap",
            }}
        >
            <Icon size={13} />
            {outcome.label}
        </span>
    );
};

const TimelineStep = ({ outcome, index, outcomes }) => {
    const Icon = outcome.icon;
    const nextOutcome = outcomes[index + 1];
    const connectorColor = outcome.state === "positive" && nextOutcome?.state === "positive" ? "#22c55e" : "#d1d5db";

    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                position: "relative",
                zIndex: 1,
                minWidth: 0,
            }}
        >
            {index < outcomes.length - 1 && (
                <div
                    style={{
                        position: "absolute",
                        top: 23,
                        left: "50%",
                        width: "100%",
                        height: 3,
                        backgroundColor: connectorColor,
                        borderRadius: 2,
                        zIndex: -1,
                    }}
                />
            )}
            <div
                style={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    border: `3px solid ${outcome.color}`,
                    backgroundColor: "#fff",
                    boxShadow: "0 2px 8px rgba(17,24,39,0.08)",
                }}
            >
                <Icon size={22} color={outcome.color} />
            </div>
            <span
                style={{
                    marginTop: 10,
                    fontSize: 12,
                    fontWeight: 700,
                    textAlign: "center",
                    color: outcome.color,
                    lineHeight: 1.25,
                }}
            >
                {outcome.label}
            </span>
            <span
                style={{
                    marginTop: 4,
                    minHeight: 14,
                    fontSize: 10,
                    fontWeight: 700,
                    color: "#6b7280",
                    textAlign: "center",
                    textTransform: "uppercase",
                    lineHeight: 1.2,
                }}
            >
                {outcome.raw || outcome.title}
            </span>
        </div>
    );
};

const TrackingSummary = ({ candidature }) => {
    const outcomes = getOutcomes(candidature);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const refusalDetails = getRefusalDetails(candidature);

    return (
        <div
            style={{
                backgroundColor: "#fff",
                borderRadius: 16,
                border: "1px solid #e8eef8",
                padding: "24px 28px 22px",
                boxShadow: "0 2px 10px rgba(32,67,133,0.06)",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 28 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ backgroundColor: "#e8eef8", padding: 8, borderRadius: 8 }}>
                        <FileCheck size={20} color="#2f4595" />
                    </div>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#1f2937" }}>
                        Tracking candidature
                    </h3>
                </div>
                <button
                    type="button"
                    onClick={() => setDetailsOpen((value) => !value)}
                    style={{
                        border: "1px solid #a3b0d2",
                        backgroundColor: detailsOpen ? "#2f4595" : "#fff",
                        color: detailsOpen ? "#fff" : "#2f4595",
                        borderRadius: 8,
                        padding: "7px 12px",
                        fontSize: 12,
                        fontWeight: 700,
                        cursor: "pointer",
                        whiteSpace: "nowrap",
                    }}
                >
                    {detailsOpen ? "Masquer les détails" : "Plus de détails"}
                </button>
            </div>

            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                    alignItems: "flex-start",
                    position: "relative",
                    padding: "4px 10px 2px",
                }}
            >
                {outcomes.map((outcome, index) => (
                    <TimelineStep key={outcome.key} outcome={outcome} index={index} outcomes={outcomes} />
                ))}
            </div>

            {detailsOpen && (
                <div
                    style={{
                        marginTop: 24,
                        borderTop: "1px solid #e8eef8",
                        paddingTop: 18,
                    }}
                >
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                            gap: 12,
                        }}
                    >
                        <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 10, padding: 12 }}>
                            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 700, marginBottom: 6 }}>
                                Manager demandeur
                            </div>
                            <div style={{ fontSize: 13, color: "#111827", fontWeight: 700 }}>
                                {getDemandeurName(candidature)}
                            </div>
                        </div>
                        <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 10, padding: 12 }}>
                            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 700, marginBottom: 6 }}>
                                Responsable RH associé
                            </div>
                            <div style={{ fontSize: 13, color: "#111827", fontWeight: 700 }}>
                                {getResponsableRhName(candidature)}
                            </div>
                        </div>
                        <div style={{ backgroundColor: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 10, padding: 12 }}>
                            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 700, marginBottom: 6 }}>
                                Intervenants désignés
                            </div>
                            <div style={{ fontSize: 13, color: "#111827", fontWeight: 700 }}>
                                {getIntervenantsLabel(candidature)}
                            </div>
                        </div>
                    </div>

                    {refusalDetails && (
                        <div
                            style={{
                                marginTop: 12,
                                backgroundColor: "#fff7f7",
                                border: "1px solid #fecaca",
                                borderRadius: 10,
                                padding: 12,
                            }}
                        >
                            <div style={{ fontSize: 12, color: "#991b1b", fontWeight: 800, marginBottom: 6 }}>
                                Étape refusée : {refusalDetails.title}
                            </div>
                            <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.45 }}>
                                <strong>Motif :</strong>{" "}
                                {refusalDetails.motif || "Motif non renseigné"}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const getCardBorder = (candidature) => {
    const outcomes = getOutcomes(candidature);
    if (outcomes.some((outcome) => outcome.state === "negative")) return "#fca5a5";
    if (outcomes.every((outcome) => outcome.state === "positive")) return "#86efac";
    return "#a3b0d2";
};

export default function HistoriqueModal({ open, onClose, candidat, historique = [] }) {
    const [trackingCandidature, setTrackingCandidature] = useState(null);

    if (!open || !candidat) return null;

    const fullName = getCandidatName(candidat);

    return (
        <div
            style={{
                position: "fixed",
                inset: 0,
                zIndex: 9999,
                backgroundColor: "rgba(0,0,0,0.45)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(2px)",
            }}
            onMouseDown={onClose}
        >
            <div
                style={{
                    backgroundColor: "#fff",
                    borderRadius: 16,
                    boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
                    width: "min(780px, 95vw)",
                    maxHeight: "85vh",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                }}
                onMouseDown={(e) => e.stopPropagation()}
            >
                <div
                    style={{
                        padding: "18px 24px",
                        borderBottom: "1px solid #e8eef8",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        backgroundColor: "#f0f4ff",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                        {trackingCandidature && (
                            <button
                                onClick={() => setTrackingCandidature(null)}
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                    background: "none",
                                    border: "1px solid #d1d5db",
                                    borderRadius: 8,
                                    padding: "4px 10px",
                                    fontSize: 13,
                                    cursor: "pointer",
                                    color: "#6b7280",
                                }}
                                type="button"
                            >
                                <ChevronLeft size={14} /> Retour
                            </button>
                        )}
                        <div>
                            <p style={{ margin: 0, fontSize: 11, color: "#9ca3af", fontWeight: 500 }}>
                                {trackingCandidature ? "Tracking" : "Historique des candidatures"}
                            </p>
                            <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "#1f2937" }}>
                                {trackingCandidature ? getPoste(trackingCandidature) : fullName}
                            </h2>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: "none",
                            border: "none",
                            cursor: "pointer",
                            color: "#9ca3af",
                            padding: 4,
                            borderRadius: 6,
                            display: "flex",
                            alignItems: "center",
                        }}
                        type="button"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div style={{ flex: 1, overflowY: "auto", padding: "20px 24px" }}>
                    {trackingCandidature ? (
                        <div>
                            <div
                                style={{
                                    padding: 14,
                                    backgroundColor: "#f0f4ff",
                                    borderRadius: 10,
                                    marginBottom: 14,
                                    border: "1px solid #e8eef8",
                                    display: "grid",
                                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                                    gap: 10,
                                }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <Briefcase size={15} color="#2f4595" />
                                    <span style={{ fontSize: 13, fontWeight: 700, color: "#2f4595" }}>
                                        {getPoste(trackingCandidature)}
                                    </span>
                                </div>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <Calendar size={15} color="#6b7280" />
                                    <span style={{ fontSize: 13, color: "#6b7280" }}>
                                        {formatDate(trackingCandidature.datePostulation || trackingCandidature.createdAt)}
                                    </span>
                                </div>
                            </div>
                            <TrackingSummary candidature={trackingCandidature} />
                        </div>
                    ) : historique.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "40px 0", color: "#9ca3af" }}>
                            <FileCheck size={36} style={{ marginBottom: 10 }} />
                            <p style={{ fontWeight: 600, fontSize: 15 }}>Aucun historique de candidature</p>
                        </div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            {historique.map((candidature) => (
                                <div
                                    key={candidature.id}
                                    style={{
                                        border: `2px solid ${getCardBorder(candidature)}`,
                                        borderRadius: 12,
                                        padding: "14px 16px",
                                        backgroundColor: "#fff",
                                        boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
                                    }}
                                >
                                    <div
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-start",
                                            gap: 12,
                                            marginBottom: 12,
                                        }}
                                    >
                                        <div>
                                            <p style={{ margin: "0 0 4px", fontWeight: 700, fontSize: 14, color: "#2f4595" }}>
                                                {getPoste(candidature)}
                                            </p>
                                            <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>
                                                {formatDate(candidature.datePostulation || candidature.createdAt)}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setTrackingCandidature(candidature)}
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 6,
                                                padding: "6px 14px",
                                                backgroundColor: "#2f4595",
                                                color: "#fff",
                                                border: "none",
                                                borderRadius: 8,
                                                fontSize: 12,
                                                fontWeight: 600,
                                                cursor: "pointer",
                                                whiteSpace: "nowrap",
                                            }}
                                            type="button"
                                        >
                                            <User size={13} /> Voir tracking
                                        </button>
                                    </div>

                                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                                        {getOutcomes(candidature).map((outcome) => (
                                            <OutcomeBadge key={outcome.key} outcome={outcome} />
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
